import { t } from 'i18next';
import _ from 'lodash';
import { ComponentType, useCallback } from 'react';
import { findBySlug, sortByName } from '../../../../../../shared/frontend/libs/utils';
import { SpecialTags } from '../../../../../../shared/types/articleTypes';
import { TArticleContentSummary, TArticleContentSummaryInfo } from '../../../../shared/newsArticleShared';
import { canPerformRole } from '../../../../shared/utils';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import frontendStore from '../../../../../../shared/frontend/libs/frontendStore';
import { getRegion } from '../container/raidFloatingMenu';
import { ClassIcon } from '../../../../../../shared/frontend/scripts/wow/components/ClassIcon';
import { DropdownTitle } from '../../../../../../shared/frontend/components/menu/dropdownTitle';
import { SpecIcon } from '../../../../../../shared/frontend/scripts/wow/components/SpecIcon';
import { TSiteConfigState, TSiteMetaClientState } from '../../libs/store';
import { EMenuEntry, TMenuEntry } from '../../../../../../shared/frontend/components/menu/menuHelper';
import { useKeystoneAffixesHelper } from '../../../../../../shared/frontend/scripts/selectors/global';
import { createSpecPageSlug } from '../../specs/shared/specRoute';
import { SpecStatisticsSurface } from '../../specs/types/surfaces';

export function getRaidRegionSubMenuItemsMenuEntry(props: any): TMenuEntry {
  let regionsChildren: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      title: t('regions:world.name'),
      link: resolveRoutePath(props.path, buildRouteParams(props.match.params, { noRealm: true, region: 'world' }))
    },
    { type: EMenuEntry.DIVIDER },
    {
      type: EMenuEntry.ITEM,
      title: t('regions:americas.name'),
      link: resolveRoutePath(props.path, buildRouteParams(props.match.params, { noRealm: true, region: 'americas' })),
      children: buildSubMenu(
        [
          ['americas', t('nav:regions.all-americas')],
          ['north-america', t('regions:north-america.name')],
          ['south-america', t('regions:south-america.name')],
          ['brazil', t('regions:portuguese.name')],
          ['us-spanish', t('regions:spanish.name')],
          ['us-central', t('regions:us-central.name')],
          ['us-eastern', t('regions:us-eastern.name')],
          ['us-mountain', t('regions:us-mountain.name')],
          ['us-pacific', t('regions:us-pacific.name')]
        ],
        props.path,
        props.match.params
      )
    },
    { type: EMenuEntry.DIVIDER },
    {
      type: EMenuEntry.ITEM,
      title: t('regions:eu.name'),
      link: resolveRoutePath(props.path, buildRouteParams(props.match.params, { noRealm: true, region: 'eu' })),
      children: buildSubMenu(
        [
          ['eu', t('nav:regions.all-europe')],
          ['eu-english', t('regions:english.name')],
          ['french', t('regions:french.name')],
          ['german', t('regions:german.name')],
          ['italian', t('regions:italian.name')],
          ['eu-portuguese', t('regions:portuguese.name')],
          ['russian', t('regions:russian.name')],
          ['eu-spanish', t('regions:spanish.name')]
        ],
        props.path,
        props.match.params
      )
    }
  ];

  const miscRegions: TMenuEntry[] = [
    ['brazil', t('regions:brazil.name')],
    ['oceanic', t('regions:oceanic.name')],
    ['kr', t('regions:kr.name')],
    ['tw', t('regions:tw.name')],
    ['cn', t('regions:cn.name')]
  ].map(([slug, name]) => ({
    type: EMenuEntry.ITEM,
    title: name,
    link: resolveRoutePath(props.path, buildRouteParams(props.match.params, { noRealm: true, region: slug }))
  }));

  regionsChildren = regionsChildren.concat(miscRegions);

  regionsChildren = [
    ...regionsChildren,
    { type: EMenuEntry.DIVIDER },
    {
      type: EMenuEntry.ITEM,
      title: t('nav:regions.global-languages'),
      children: buildSubMenu(
        [
          ['tw', t('nav:regions:chinese')],
          ['english', t('regions:english.name')],
          ['french', t('regions:french.name')],
          ['german', t('regions:german.name')],
          ['italian', t('regions:italian.name')],
          ['kr', t('nav:regions.korean')],
          ['portuguese', t('regions:portuguese.name')],
          ['russian', t('regions:russian.name')],
          ['spanish', t('regions:spanish.name')]
        ],
        props.path,
        props.match.params
      )
    },
    {
      type: EMenuEntry.ITEM,
      title: t('nav:regions.wow-regions'),
      children: buildSubMenu(
        [
          ['us', t('regions:us.name')],
          ['eu', t('regions:eu.name')],
          ['kr', t('regions:kr.name')],
          ['tw', t('regions:tw.name')],
          ['cn', t('regions:cn.name')]
        ],
        props.path,
        props.match.params
      )
    }
  ];

  const baseMenu = {
    type: EMenuEntry.ITEM,
    title: (
      <DropdownTitle
        title={
          props.match.params.region
            ? t(`regions:${getRegion(props.combinedRegions, props.match.params.region).slug}.name`)
            : 'Region'
        }
      />
    ),
    children: regionsChildren
  };

  return baseMenu;
}

export function getClass(classes: any, cls: any) {
  return findBySlug(classes, cls.toLowerCase());
}

export type TFormatAffixesRoute = (affixes: string, useShort?: boolean) => string;

export const useFormatAffixesRoute = () => {
  const keystoneAffixHelper = useKeystoneAffixesHelper();

  return useCallback<TFormatAffixesRoute>(
    function formatAffixesRoute(affixes, useShort) {
      if (!affixes) {
        return '';
      }

      const affixesMap = keystoneAffixHelper.getDirtyAffixSlugIdMap();

      if (!affixesMap || Object.keys(affixesMap).length === 0) {
        return affixes;
      }

      for (const [slug, id] of Object.entries(affixesMap)) {
        affixes = affixes.replace(slug, useShort ? t(`affixes:${id}.short-name`) : t(`affixes:${id}.name`));
      }

      return affixes
        .split('-')
        .map((afx: string) =>
          afx
            .split(' ')
            .map((a) => _.capitalize(a))
            .join(' ')
        )
        .join(', ');
    },
    [keystoneAffixHelper]
  );
};

export const withFormatAffixesRoute = (Component: ComponentType<any>) => {
  return (props: any) => {
    const formatAffixesRoute = useFormatAffixesRoute();
    return <Component {...props} formatAffixesRoute={formatAffixesRoute} />;
  };
};

export function buildRouteParams(
  current: Record<any, any>,
  overrides?: Record<any, any>,
  resetPagination: boolean = true
) {
  if (resetPagination && overrides && !overrides.page) {
    current = _.omit(current, ['page']);
  }
  const state = frontendStore.getState<TSiteMetaClientState, TSiteConfigState>();
  return _.extend(
    {
      // for raids
      raid: state.siteConfig.currentRaidSlug,
      difficulty: state.siteConfig.currentRaidDifficulty,
      region: 'world',

      // for M+
      season: state.siteConfig.currentSeasonSlug,
      dungeon: 'all',
      type: 'leaderboards-strict',
      class: 'all',
      role: 'all'
    },
    _.omitBy(current, _.isNil),
    overrides
  );
}

export function getCharacterSubMenuItems(props: any, opt?: any, overrides?: any): TMenuEntry[] {
  const path = opt && opt.path ? opt.path : namedRoutes.KeystoneCharacterRankings;
  const sortedClasses = [...props.classes].sort(sortByName);
  return [
    {
      type: EMenuEntry.ITEM,
      title: t('nav:all-classes-and-roles'),
      link: resolveRoutePath(path, buildRouteParams({ ...props.match.params, class: 'all', role: 'all' }, overrides)),
      children: [
        {
          type: EMenuEntry.ITEM,
          title: t('nav:all-roles'),
          link: resolveRoutePath(
            path,
            buildRouteParams({ ...props.match.params, class: 'all', role: 'all' }, overrides)
          ),
          iconClassName: 'icon-role icon-x-small any-lfg'
        },
        {
          type: EMenuEntry.DIVIDER
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:all-role-plural', {
            role: t('common:roles.tank.name', { count: 100 })
          }),
          link: resolveRoutePath(
            path,
            buildRouteParams({ ...props.match.params, class: 'all', role: 'tank' }, overrides)
          ),
          iconClassName: 'icon-role icon-x-small tank-lfg'
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:all-role-plural', {
            role: t('common:roles.healer.name', { count: 100 })
          }),
          link: resolveRoutePath(
            path,
            buildRouteParams({ ...props.match.params, class: 'all', role: 'healer' }, overrides)
          ),
          iconClassName: 'icon-role icon-x-small healer-lfg'
        },
        {
          type: EMenuEntry.ITEM,
          title: t('nav:all-role-plural', {
            role: t('common:roles.dps.name', { count: 100 })
          }),
          link: resolveRoutePath(
            path,
            buildRouteParams({ ...props.match.params, class: 'all', role: 'dps' }, overrides)
          ),
          iconClassName: 'icon-role icon-x-small dps-lfg'
        },
        {
          type: EMenuEntry.DIVIDER
        }
      ]
    },
    ...sortedClasses.map<TMenuEntry>((cls: any) => {
      const canHeal = canPerformRole(cls.id, 'healer');
      const canTank = canPerformRole(cls.id, 'tank');

      const item: TMenuEntry = {
        type: EMenuEntry.ITEM,
        link: resolveRoutePath(
          path,
          buildRouteParams({ ...props.match.params, class: cls.slug, role: 'all' }, overrides)
        ),
        title: t(`game:classes.${cls.slug}.name`),
        customIconComponent: <ClassIcon class={cls.slug} />,
        titleClass: `class-color--${cls.id}`
      };

      if (canHeal || canTank) {
        item.children = [];

        item.children.push({
          type: EMenuEntry.ITEM,
          link: resolveRoutePath(
            path,
            buildRouteParams({ ...props.match.params, class: cls.slug, role: 'all' }, overrides)
          ),
          title: t('nav:all-roles'),
          iconClassName: 'icon-role icon-x-small any-lfg'
        });

        item.children.push({
          type: EMenuEntry.DIVIDER
        });

        if (canTank) {
          item.children.push({
            type: EMenuEntry.ITEM,
            link: resolveRoutePath(
              path,
              buildRouteParams({ ...props.match.params, class: cls.slug, role: 'tank' }, overrides)
            ),
            title: t('common:roles.tank.name', { count: 100 }),
            iconClassName: 'icon-role icon-x-small tank-lfg'
          });
        }

        if (canHeal) {
          item.children.push({
            type: EMenuEntry.ITEM,
            link: resolveRoutePath(
              path,
              buildRouteParams({ ...props.match.params, class: cls.slug, role: 'healer' }, overrides)
            ),
            title: t('common:roles.healer.name', { count: 100 }),
            iconClassName: 'icon-role icon-x-small healer-lfg'
          });
        }

        if (canHeal || canTank) {
          item.children.push({
            type: EMenuEntry.ITEM,
            link: resolveRoutePath(
              path,
              buildRouteParams({ ...props.match.params, class: cls.slug, role: 'dps' }, overrides)
            ),
            title: t('common:roles.dps.name', { count: 100 }),
            iconClassName: 'icon-role icon-x-small dps-lfg'
          });
        }
      }

      return item;
    })
  ];
}

export function getWarbandSubMenuItems(props: any): TMenuEntry[] {
  const path = namedRoutes.KeystoneWarbandRankings;
  return [
    {
      type: EMenuEntry.ITEM,
      title: t('nav:all-roles'),
      link: resolveRoutePath(
        path,
        buildRouteParams({ ...props.match.params, class: 'all', role: 'all', region: 'world' })
      ),
      iconClassName: 'icon-role icon-x-small any-lfg'
    },
    {
      type: EMenuEntry.DIVIDER
    },
    {
      type: EMenuEntry.ITEM,
      title: t('nav:only-role-plural', {
        role: t('common:roles.tank.name', { count: 100 })
      }),
      link: resolveRoutePath(
        path,
        buildRouteParams({ ...props.match.params, class: 'all', role: 'tank', region: 'world' })
      ),
      iconClassName: 'icon-role icon-x-small tank-lfg'
    },
    {
      type: EMenuEntry.ITEM,
      title: t('nav:only-role-plural', {
        role: t('common:roles.healer.name', { count: 100 })
      }),
      link: resolveRoutePath(
        path,
        buildRouteParams({ ...props.match.params, class: 'all', role: 'healer', region: 'world' })
      ),
      iconClassName: 'icon-role icon-x-small healer-lfg'
    },
    {
      type: EMenuEntry.ITEM,
      title: t('nav:only-role-plural', {
        role: t('common:roles.dps.name', { count: 100 })
      }),
      link: resolveRoutePath(
        path,
        buildRouteParams({ ...props.match.params, class: 'all', role: 'dps', region: 'world' })
      ),
      iconClassName: 'icon-role icon-x-small dps-lfg'
    }
  ];
}

export function getSpecSubMenuItems(props: any, opt?: any, overrides?: any): TMenuEntry[] {
  const path = opt && opt.path ? opt.path : namedRoutes.KeystoneSpecRankings;
  return [...props.classes].sort(sortByName).map<TMenuEntry>((cls: any) => {
    const classSpecs = props.specs.filter((spec: any) => spec.class_id === cls.id);
    return {
      type: EMenuEntry.ITEM,
      title: t(`game:classes.${cls.slug}.name`),
      customIconComponent: <ClassIcon class={cls.slug} />,
      titleClass: `class-color--${cls.id}`,
      ...(classSpecs.length > 0
        ? {
            link: resolveRoutePath(
              path,
              buildRouteParams({ ...props.match.params, class: cls.slug, spec: classSpecs[0].slug }, overrides)
            )
          }
        : {}),
      children: classSpecs.map(
        (spec: any): TMenuEntry => ({
          type: EMenuEntry.ITEM,
          link: resolveRoutePath(
            path,
            buildRouteParams({ ...props.match.params, class: cls.slug, spec: spec.slug }, overrides)
          ),
          customIconComponent: <SpecIcon class={cls.slug} spec={spec.slug} />,
          title: t(`game:specs.${cls.slug}.${spec.slug}.name`)
        })
      )
    };
  });
}

export function getTalentBuildsSubMenuItems(props: any): TMenuEntry[] {
  const getSpecTalentsLink = (cls: any, spec: any) =>
    resolveRoutePath(namedRoutes.SpecStatistics, {
      specSlug: createSpecPageSlug(cls.slug, spec.slug),
      type: SpecStatisticsSurface.Talents
    });

  return [...props.classes].sort(sortByName).map<TMenuEntry>((cls: any) => {
    const classSpecs = props.specs.filter((spec: any) => spec.class_id === cls.id);
    return {
      type: EMenuEntry.ITEM,
      title: t(`game:classes.${cls.slug}.name`),
      customIconComponent: <ClassIcon class={cls.slug} />,
      titleClass: `class-color--${cls.id}`,
      ...(classSpecs.length > 0 ? { link: getSpecTalentsLink(cls, classSpecs[0]) } : {}),
      children: classSpecs.map(
        (spec: any): TMenuEntry => ({
          type: EMenuEntry.ITEM,
          link: getSpecTalentsLink(cls, spec),
          customIconComponent: <SpecIcon class={cls.slug} spec={spec.slug} />,
          title: t(`game:specs.${cls.slug}.${spec.slug}.name`)
        })
      )
    };
  });
}

interface IGetArticleSubMenuItemProps {
  type: SpecialTags;
  isSelected: boolean;
  isLatestArticle: boolean;
  article: TArticleContentSummaryInfo;
}

export function getArticleSubMenuItem(props: IGetArticleSubMenuItemProps) {
  switch (props.type) {
    case SpecialTags.WeeklyRoute: {
      return {
        type: EMenuEntry.ITEM,
        link: props.isLatestArticle ? resolveRoutePath(namedRoutes.WeeklyRoutes) : props.article.path,
        className: props.isSelected ? 'slds-text-heading--small text-raiderio text-bold' : 'slds-text-heading--small',
        wowIcon: props.article.season.icon,
        title: props.article.title
      };
    }
    case SpecialTags.PlayLikeThePros: {
      return {
        type: EMenuEntry.ITEM,
        link: props.isLatestArticle ? resolveRoutePath(namedRoutes.PlayLikeThePros) : props.article.path,
        className: props.isSelected ? 'slds-text-heading--small text-raiderio text-bold' : 'slds-text-heading--small',
        wowIcon: props.article.season.icon,
        title: props.article.affixes.join(', ') || props.article.title.replace(/Play Like the Pros: /i, '')
      };
    }
    default: {
      return null;
    }
  }
}

interface IGetArticleSubMenuItemsProps {
  type: SpecialTags;
  lastArticle: TArticleContentSummary;
  selectedArticle: TArticleContentSummary;
  articles: TArticleContentSummaryInfo[];
}

export function getArticleSubMenuItems(props: IGetArticleSubMenuItemsProps): TMenuEntry[] {
  return props.articles.reduce((accum: TMenuEntry[], article: any) => {
    const isSelected = props.selectedArticle.id === article.id;
    const isLatestArticle = props.lastArticle?.id === article.id;
    if (!article.season) {
      return accum;
    }

    const item = getArticleSubMenuItem({
      type: props.type,
      isLatestArticle,
      isSelected,
      article
    });

    if (!item) {
      return accum;
    }

    if (article.season === props.articles[0].season) {
      // just add it directly
      accum.push(item);
    } else {
      // otherwise put it in a submenu, but find which first
      let existingSeason: any = null;
      for (const existingItem of accum) {
        if ((existingItem as any).children && (existingItem as any).title === article.season.title) {
          existingSeason = existingItem;
        }
      }
      if (!existingSeason) {
        existingSeason = {
          type: EMenuEntry.ITEM,
          wowIcon: article.season.icon,
          title: article.season.title,
          className: 'slds-text-heading--small',
          children: []
        };
        accum.push(existingSeason);
      }
      existingSeason.children.push(item);
    }

    return accum;
  }, [] as TMenuEntry[]);
}

type TSlugWithTitle = [string, string][];

function buildSubMenu(slugWithTitle: TSlugWithTitle, path: string, params: any): TMenuEntry[] {
  return slugWithTitle.map(([slug, name]) => ({
    type: EMenuEntry.ITEM,
    link: resolveRoutePath(path, buildRouteParams(params, { noRealm: true, region: slug })),
    title: name
  }));
}
